import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TableBody,
  TableRow,
  CircularProgress,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Tooltip,
  Checkbox,
  IconButton,
  Typography,
  Collapse,
  TableSortLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';
import { tableRowTransparentSelectionSx } from '../../styles/theme/tableRowOverlays';
import {
  SearchOutlined as SearchIcon,
  EditOutlined as EditIcon,
  RefreshOutlined as RefreshIcon,
  AddLocationAltOutlined as AddLocationIcon,
  TuneOutlined as TuneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import HoverExpandAction from './HoverExpandAction';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageFiltersSection,
  ListPageFilterContainer,
  ListPageFilterRow,
  ListPageMainSearchField,
  ListPageFilterTextField,
  ListPageFilterToolbarOutlinedButton,
  ListPageFilterToolbarSearchButton,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../components/common/list-page';
import {
  TableContainerStyled,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
  StyledTable,
  StyledTableContainer,
  StatusChip as TableStatusChip,
  ActionButton,
} from '../../components/common/Table/Table.styles';
import CommonPagination from '../../components/common/Pagination';
import EmptyStateMessage from '../../components/common/EmptyStateMessage';
import CatalogAutocomplete from '../../components/common/CatalogAutocomplete';
import { SafetyStockService, type SafetyStockRow } from '../../api/services/SafetyStockService';
import { LocationsService } from '../../api/services/LocationsService';
import { CategoriesService } from '../../api/services/CategoriesService';
import type { Location } from '../../api/models/Location';
import type { Category } from '../../api/models/Category';
import EditSafetyStockModal from './EditSafetyStockModal';
import AddSafetyStockLocationModal from './AddSafetyStockLocationModal';
import BulkUpdateSafetyStockModal from './BulkUpdateSafetyStockModal';
import BulkUpdateSelectedSafetyStockModal from './BulkUpdateSelectedSafetyStockModal';
import { safetyStockSelectionKey } from './safetyStockSelection.utils';
import { usePermission } from '../../hooks/usePermission';
import { INVENTORY_PERMISSIONS } from '../../constants/permissions.constants';

const ESTADO_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  NORMAL: 'success',
  BAJO_STOCK: 'warning',
  SIN_STOCK: 'error',
  NULO: 'default',
};

const ESTADO_LABELS: Record<string, string> = {
  NORMAL: 'Normal',
  BAJO_STOCK: 'Bajo stock',
  SIN_STOCK: 'Sin stock',
  NULO: 'Sin configurar',
};

type SafetyStockSortField =
  | 'productCode'
  | 'productName'
  | 'tipoProducto'
  | 'sucursalName'
  | 'stockDisponible'
  | 'stockSeguridad'
  | 'estado';

const SAFETY_STOCK_SORT_COLUMNS: {
  field: SafetyStockSortField;
  label: string;
  align?: 'left' | 'right';
}[] = [
  { field: 'productCode', label: 'Código' },
  { field: 'productName', label: 'Nombre' },
  { field: 'tipoProducto', label: 'Tipo producto' },
  { field: 'sucursalName', label: 'Ubicación' },
  { field: 'stockDisponible', label: 'Stock Disponible', align: 'right' },
  { field: 'stockSeguridad', label: 'Stock de seguridad', align: 'right' },
  { field: 'estado', label: 'Estado' },
];

const SafetyStockPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const canEdit = usePermission(INVENTORY_PERMISSIONS.SAFETY_STOCK.EDIT);
  const [data, setData] = useState<SafetyStockRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editModalRow, setEditModalRow] = useState<SafetyStockRow | null>(null);
  const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedRowsDetails, setSelectedRowsDetails] = useState<SafetyStockRow[]>([]);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const [filters, setFilters] = useState({
    search: '',
    locationId: '',
    productType: '',
    estado: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const appliedFiltersRef = useRef(filters);
  const skipFetchAfterUrlSync = useRef(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableSort, setTableSort] = useState<{
    sortBy: SafetyStockSortField;
    sortOrder: 'asc' | 'desc';
  }>({ sortBy: 'productCode', sortOrder: 'asc' });
  const tableSortRef = useRef(tableSort);
  tableSortRef.current = tableSort;

  const fetchLocations = useCallback(async () => {
    try {
      const res = await LocationsService.locationsControllerFindAll();
      const list = Array.isArray(res) ? res : ((res as { data?: Location[] })?.data ?? []);
      const forSafetyStock = list
        .filter((l: Location) => l.type === 'BRANCH' || l.type === 'WAREHOUSE')
        .sort((a, b) => {
          if (a.type !== b.type) {
            return a.type === 'WAREHOUSE' ? -1 : 1;
          }
          return a.name.localeCompare(b.name, 'es');
        });
      setLocations(forSafetyStock);
    } catch {
      setLocations([]);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await CategoriesService.categoriesControllerFindAll(1, 1000);
      const list = Array.isArray(res) ? res : ((res as { data?: Category[] })?.data ?? []);
      setCategories(list);
    } catch {
      setCategories([]);
    }
  }, []);

  const fetchData = useCallback(
    async (overrides?: {
      page?: number;
      limit?: number;
      filters?: typeof filters;
      sort?: { sortBy: SafetyStockSortField; sortOrder: 'asc' | 'desc' };
    }) => {
      const effectivePage = overrides?.page ?? page;
      const effectiveLimit = overrides?.limit ?? limit;
      const effectiveFilters = overrides?.filters ?? appliedFiltersRef.current;
      const effectiveSort = overrides?.sort ?? tableSortRef.current;

      setIsLoading(true);
      try {
        const estadoParam =
          effectiveFilters.estado === 'NORMAL' ||
          effectiveFilters.estado === 'BAJO_STOCK' ||
          effectiveFilters.estado === 'SIN_STOCK' ||
          effectiveFilters.estado === 'NULO'
            ? effectiveFilters.estado
            : undefined;
        const res = await SafetyStockService.productLocationSafetyStockControllerFindAll(
          effectivePage,
          effectiveLimit,
          effectiveFilters.search || undefined,
          effectiveFilters.locationId ? Number(effectiveFilters.locationId) : undefined,
          effectiveFilters.productType || undefined,
          estadoParam,
          effectiveSort.sortBy,
          effectiveSort.sortOrder,
        );
        setData(res.data ?? []);
        setTotal(res.total ?? 0);
      } catch (err) {
        setData([]);
        setTotal(0);
        setNotification({
          open: true,
          message:
            err instanceof Error ? err.message : 'Error al cargar los datos de stock de seguridad.',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [page, limit],
  );

  useEffect(() => {
    fetchLocations();
    fetchCategories();
  }, [fetchLocations, fetchCategories]);

  useEffect(() => {
    if (skipFetchAfterUrlSync.current) {
      skipFetchAfterUrlSync.current = false;
      return;
    }

    const params = new URLSearchParams(location.search);
    const productCode = params.get('productCode');
    const locationIdParam = params.get('locationId');
    if (productCode || locationIdParam) {
      const newFilters = {
        search: productCode ?? '',
        locationId: locationIdParam ?? '',
        productType: '',
        estado: '',
      };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      appliedFiltersRef.current = newFilters;
      setPage(1);
      fetchData({ page: 1, limit, filters: newFilters });
      skipFetchAfterUrlSync.current = true;
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }

    fetchData();
  }, [fetchData, location.search, location.pathname, navigate, limit]);

  const handleSearch = () => {
    appliedFiltersRef.current = filters;
    setAppliedFilters(filters);
    setPage(1);
    fetchData({ page: 1, limit, filters });
  };

  const handleReset = () => {
    const newFilters = { search: '', locationId: '', productType: '', estado: '' };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    appliedFiltersRef.current = newFilters;
    setPage(1);
    fetchData({ page: 1, limit, filters: newFilters });
  };

  const clearAppliedFilterField = useCallback(
    (field: 'search' | 'locationId' | 'productType' | 'estado') => {
      const next = { ...appliedFiltersRef.current, [field]: '' };
      setFilters(next);
      setAppliedFilters(next);
      appliedFiltersRef.current = next;
      setPage(1);
      fetchData({ page: 1, limit, filters: next });
    },
    [fetchData, limit],
  );

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.search) {
      chips.push({
        key: 'search',
        label: `Búsqueda: ${applied.search}`,
        onRemove: () => clearAppliedFilterField('search'),
      });
    }
    if (applied.locationId) {
      const locName =
        locations.find((l) => String(l.id) === applied.locationId)?.name || applied.locationId;
      chips.push({
        key: 'locationId',
        label: `Ubicación: ${locName}`,
        onRemove: () => clearAppliedFilterField('locationId'),
      });
    }
    if (applied.productType) {
      chips.push({
        key: 'productType',
        label: `Tipo: ${applied.productType}`,
        onRemove: () => clearAppliedFilterField('productType'),
      });
    }
    if (applied.estado) {
      chips.push({
        key: 'estado',
        label: `Estado: ${ESTADO_LABELS[applied.estado] ?? applied.estado}`,
        onRemove: () => clearAppliedFilterField('estado'),
      });
    }

    return chips;
  }, [appliedFilters, locations, clearAppliedFilterField]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleItemsPerPageChange = (e: SelectChangeEvent<unknown>) => {
    setLimit(Number(e.target.value) || 10);
    setPage(1);
  };

  const handleEditSuccess = useCallback(() => {
    fetchData();
    setNotification({
      open: true,
      message: 'Stock de seguridad actualizado con éxito.',
      severity: 'success',
    });
  }, [fetchData]);

  const handleAddLocationSuccess = useCallback(() => {
    fetchData();
    setNotification({
      open: true,
      message: 'Mínimo agregado para la ubicación seleccionada.',
      severity: 'success',
    });
  }, [fetchData]);

  const handleBulkSuccess = useCallback(
    (updatedCount?: number) => {
      fetchData();
      setNotification({
        open: true,
        message:
          updatedCount != null && updatedCount > 0
            ? `Se actualizaron ${updatedCount} registro${updatedCount === 1 ? '' : 's'} de stock de seguridad.`
            : 'Stock actualizado con éxito.',
        severity: 'success',
      });
    },
    [fetchData],
  );

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSearchFieldKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSortChange = useCallback(
    (field: SafetyStockSortField) => {
      const prev = tableSortRef.current;
      const next =
        prev.sortBy === field
          ? {
              sortBy: field,
              sortOrder: (prev.sortOrder === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc',
            }
          : { sortBy: field, sortOrder: 'asc' as const };
      tableSortRef.current = next;
      setTableSort(next);
      setPage(1);
      void fetchData({ page: 1, limit, sort: next });
    },
    [fetchData, limit],
  );

  useEffect(() => {
    const newDetails = [...selectedRowsDetails];
    let changed = false;
    data.forEach((row) => {
      const key = safetyStockSelectionKey(row);
      if (selectedKeys.includes(key)) {
        const existingIndex = newDetails.findIndex((d) => safetyStockSelectionKey(d) === key);
        if (existingIndex === -1) {
          newDetails.push(row);
          changed = true;
        }
      }
    });
    if (changed) {
      setSelectedRowsDetails(newDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedKeys]);

  const handleSelectRow = useCallback((row: SafetyStockRow) => {
    const key = safetyStockSelectionKey(row);
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
    setSelectedRowsDetails((prev) => {
      if (prev.some((d) => safetyStockSelectionKey(d) === key)) {
        return prev.filter((d) => safetyStockSelectionKey(d) !== key);
      }
      return [...prev, row];
    });
  }, []);

  const isAllPageSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every((row) => selectedKeys.includes(safetyStockSelectionKey(row)));
  }, [data, selectedKeys]);

  const isPageSelectionIndeterminate = useMemo(() => {
    if (data.length === 0) return false;
    const selectedOnPage = data.filter((row) =>
      selectedKeys.includes(safetyStockSelectionKey(row)),
    ).length;
    return selectedOnPage > 0 && selectedOnPage < data.length;
  }, [data, selectedKeys]);

  const handleSelectAllPage = useCallback(() => {
    if (isAllPageSelected) {
      const pageKeys = new Set(data.map((row) => safetyStockSelectionKey(row)));
      setSelectedKeys((prev) => prev.filter((k) => !pageKeys.has(k)));
      setSelectedRowsDetails((prev) =>
        prev.filter((d) => !pageKeys.has(safetyStockSelectionKey(d))),
      );
    } else {
      const pageKeys = data.map((row) => safetyStockSelectionKey(row));
      setSelectedKeys((prev) => {
        const next = [...prev];
        pageKeys.forEach((k) => {
          if (!next.includes(k)) next.push(k);
        });
        return next;
      });
      setSelectedRowsDetails((prev) => {
        const next = [...prev];
        data.forEach((row) => {
          const key = safetyStockSelectionKey(row);
          if (!next.some((d) => safetyStockSelectionKey(d) === key)) {
            next.push(row);
          }
        });
        return next;
      });
    }
  }, [data, isAllPageSelected]);

  const handleOpenSelectionModal = useCallback(() => {
    if (selectedKeys.length > 0) {
      setSelectionModalOpen(true);
    }
  }, [selectedKeys.length]);

  /** One product from checkbox selection (all selected rows share productId). */
  const selectedProductForAdd = useMemo(() => {
    if (selectedRowsDetails.length === 0) return null;
    const productIds = new Set(selectedRowsDetails.map((r) => r.productId));
    if (productIds.size !== 1) return null;
    const row = selectedRowsDetails[0];
    return {
      id: row.productId,
      name: row.productName,
      code: row.productCode,
    };
  }, [selectedRowsDetails]);

  const handleClearSelection = useCallback(() => {
    setSelectedKeys([]);
    setSelectedRowsDetails([]);
  }, []);

  const handleSelectionBulkSuccess = useCallback(
    (updatedCount?: number) => {
      setSelectedKeys([]);
      setSelectedRowsDetails([]);
      setSelectionModalOpen(false);
      handleBulkSuccess(updatedCount);
    },
    [handleBulkSuccess],
  );

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageFiltersSection>
          <ListPageFilterContainer>
            <ListPageFilterRow sx={{ flexWrap: 'wrap' }}>
              <ListPageMainSearchField
                label="Buscar producto"
                placeholder="Nombre o código"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={handleSearchFieldKeyDown}
              />
              <ListPageFilterTextField
                select
                variant="outlined"
                size="medium"
                label="Ubicación"
                value={filters.locationId}
                onChange={(e) => setFilters((f) => ({ ...f, locationId: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 180 }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={String(loc.id)}>
                    {loc.name}
                  </MenuItem>
                ))}
              </ListPageFilterTextField>
              <Box sx={{ minWidth: 180 }}>
                <CatalogAutocomplete
                  catalogType="category"
                  value={filters.productType}
                  valueMode="name"
                  onChange={(value) => setFilters((f) => ({ ...f, productType: value }))}
                  pinOptions={categories}
                  label="Tipo producto"
                  size="medium"
                  textFieldProps={{ variant: 'outlined' }}
                />
              </Box>
              <ListPageFilterTextField
                select
                variant="outlined"
                size="medium"
                label="Estado"
                value={filters.estado}
                onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="NORMAL">Normal</MenuItem>
                <MenuItem value="BAJO_STOCK">Bajo stock</MenuItem>
                <MenuItem value="SIN_STOCK">Sin stock</MenuItem>
                <MenuItem value="NULO">Sin configurar</MenuItem>
              </ListPageFilterTextField>
              <ListPageFilterToolbarOutlinedButton
                variant="outlined"
                size="medium"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
              >
                Reiniciar
              </ListPageFilterToolbarOutlinedButton>
              <ListPageFilterToolbarSearchButton
                variant="contained"
                size="medium"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Buscar
              </ListPageFilterToolbarSearchButton>
              {canEdit && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                  <Tooltip title="Edición personalizada">
                    <IconButton
                      size="medium"
                      onClick={() => setBulkModalOpen(true)}
                      aria-label="Edición personalizada"
                      sx={{
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          color: 'text.primary',
                        },
                      }}
                    >
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </ListPageFilterRow>

            <ListPageActiveFilters chips={activeFilterChips} />
          </ListPageFilterContainer>
        </ListPageFiltersSection>

        <Collapse
          in={canEdit && selectedKeys.length > 0}
          timeout={{ enter: 420, exit: 300 }}
          easing={{
            enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
            exit: 'cubic-bezier(0.4, 0, 1, 1)',
          }}
          unmountOnExit
          sx={{
            '& .MuiCollapse-wrapperInner': {
              // Keeps layout stable while height animates
              display: 'block',
            },
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: 2,
              border: `1px solid ${alpha(colors.success.main, 0.22)}`,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? alpha(colors.success.main, 0.1)
                  : alpha(colors.success.main, 0.05),
              boxShadow:
                theme.palette.mode === 'dark'
                  ? `0 8px 24px ${alpha('#000', 0.25)}`
                  : `0 6px 20px ${alpha(colors.success.main, 0.08)}`,
              '@keyframes safetyStockSelectionBarIn': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-8px) scale(0.985)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0) scale(1)',
                },
              },
              animation: 'safetyStockSelectionBarIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both',
              transition:
                'box-shadow 320ms cubic-bezier(0.22, 1, 0.36, 1), border-color 320ms ease',
            })}
          >
            <Typography
              key={selectedKeys.length}
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                '@keyframes safetyStockSelectionCountIn': {
                  from: { opacity: 0.35, transform: 'translateY(2px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: 'safetyStockSelectionCountIn 280ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {selectedKeys.length} seleccionado{selectedKeys.length === 1 ? '' : 's'}
            </Typography>
            <HoverExpandAction
              label="Agregar ubicación"
              icon={<AddLocationIcon />}
              onClick={() => setAddLocationModalOpen(true)}
              disabled={!selectedProductForAdd}
              disabledTitle="Seleccione filas de una sola pieza para agregar ubicación"
              variant="secondary"
            />
            <HoverExpandAction
              label="Actualizar"
              icon={<EditIcon />}
              onClick={handleOpenSelectionModal}
              variant="primary"
            />
            <HoverExpandAction
              label="Limpiar"
              icon={<CloseIcon />}
              onClick={handleClearSelection}
              variant="ghost"
            />
          </Box>
        </Collapse>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <EmptyStateMessage message="No hay datos de stock de seguridad para mostrar." />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'hidden',
              minHeight: 0,
              px: 0,
            }}
          >
            <TableContainerStyled>
              <StyledTableContainer>
                <StyledTable size="medium">
                  <StyledTableHead>
                    <TableRow>
                      {canEdit && (
                        <StyledTableHeadCell padding="checkbox">
                          <Checkbox
                            indeterminate={isPageSelectionIndeterminate}
                            checked={isAllPageSelected && data.length > 0}
                            onChange={handleSelectAllPage}
                            size="small"
                            sx={{
                              color: colors.success.main,
                              '&.Mui-checked': { color: colors.success.main },
                              '&.MuiCheckbox-indeterminate': { color: colors.success.main },
                            }}
                          />
                        </StyledTableHeadCell>
                      )}
                      {SAFETY_STOCK_SORT_COLUMNS.map((column) => (
                        <StyledTableHeadCell key={column.field} align={column.align ?? 'left'}>
                          <TableSortLabel
                            active={tableSort.sortBy === column.field}
                            direction={
                              tableSort.sortBy === column.field ? tableSort.sortOrder : 'asc'
                            }
                            onClick={() => handleSortChange(column.field)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </StyledTableHeadCell>
                      ))}
                      <StyledTableHeadCell align="right">Acción</StyledTableHeadCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {data.map((row) => {
                      const rowKey = safetyStockSelectionKey(row);
                      const isRowSelected = selectedKeys.includes(rowKey);
                      return (
                        <StyledTableRow
                          key={rowKey}
                          hover
                          selected={false}
                          sx={tableRowTransparentSelectionSx}
                        >
                          {canEdit && (
                            <StyledTableCell padding="checkbox">
                              <Checkbox
                                checked={isRowSelected}
                                onChange={() => handleSelectRow(row)}
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  color: colors.success.main,
                                  '&.Mui-checked': { color: colors.success.main },
                                }}
                              />
                            </StyledTableCell>
                          )}
                          <StyledTableCell tone="muted">{row.productCode}</StyledTableCell>
                          <StyledTableCell tone="muted">{row.productName}</StyledTableCell>
                          <StyledTableCell tone="muted">{row.tipoProducto ?? '—'}</StyledTableCell>
                          <StyledTableCell tone="muted">{row.sucursalName}</StyledTableCell>
                          <StyledTableCell tone="muted" align="right">
                            {row.stockDisponible}
                          </StyledTableCell>
                          <StyledTableCell tone="muted" align="right">
                            {row.stockSeguridad != null ? row.stockSeguridad : '—'}
                          </StyledTableCell>
                          <StyledTableCell>
                            <TableStatusChip
                              label={
                                row.estado != null ? (ESTADO_LABELS[row.estado] ?? row.estado) : '—'
                              }
                              color={
                                (row.estado != null ? ESTADO_COLORS[row.estado] : 'default') as
                                  | 'success'
                                  | 'warning'
                                  | 'error'
                                  | 'default'
                              }
                              size="small"
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Tooltip title="Editar">
                              <span>
                                <ActionButton
                                  size="small"
                                  onClick={() => setEditModalRow(row)}
                                  disabled={!canEdit}
                                >
                                  <EditIcon fontSize="small" />
                                </ActionButton>
                              </span>
                            </Tooltip>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </StyledTable>
              </StyledTableContainer>
            </TableContainerStyled>
          </Box>
        )}

        {!isLoading && data.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={page}
              itemsPerPage={limit}
              totalItems={total}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      <EditSafetyStockModal
        open={editModalRow != null}
        onClose={() => setEditModalRow(null)}
        row={editModalRow}
        onSuccess={handleEditSuccess}
      />
      <AddSafetyStockLocationModal
        open={addLocationModalOpen}
        onClose={() => setAddLocationModalOpen(false)}
        product={selectedProductForAdd}
        locations={locations}
        onSuccess={handleAddLocationSuccess}
      />
      <BulkUpdateSafetyStockModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        locations={locations}
        categories={categories}
        onSuccess={handleBulkSuccess}
      />
      <BulkUpdateSelectedSafetyStockModal
        open={selectionModalOpen}
        onClose={() => setSelectionModalOpen(false)}
        selectedRows={selectedRowsDetails}
        onSuccess={handleSelectionBulkSuccess}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};

export default SafetyStockPage;
